/**
 * Работа остаётся на экране, даже когда кадра уже нет.
 *
 * Карточка в ленте жила, только пока строка попадала в полку или в большой
 * список. Закрепил и открыл — пауза вырезает id из ленты (он на полке), полка
 * берётся из свежего кадра. Один кадр без строки, и Detail размонтируется:
 * пустота вместо пелены, цифры потом возвращаются как ни в чём не бывало.
 * Замер 05.09: закреплённая ушла `r: closed` — карточка пропала на десятки
 * секунд, пелены не было. Сито `held` тут ни при чём: оно уже не режет работу.
 *
 * Пустота запрещена. Нет в кадре — оставляем последнюю; движок жив — кроем
 * пеленой. То же правило, что у окна (`One.tsx`).
 */

import { useRef } from 'react';

import { tapeAllowed } from './tapes';
import type { PageLine } from './route';
import type { FeedRow } from './rows';
import { heldIds, useScreen } from './store';

function lineOfRow(fork: { line?: string | null }): string | null {
  return typeof fork.line === 'string' && fork.line !== '' ? fork.line : null;
}

/** Нет в кадре — последняя известная; живой движок без строки — пелена. */
export function coverMissing(
  found: FeedRow | null,
  last: FeedRow | null,
  missAt: number | null,
  engineLive: boolean,
): FeedRow | null {
  if (found !== null) return found;
  if (last === null) return null;
  if (last.ghost !== null || !engineLive) return last;
  return {
    ...last,
    ghost: { fork: last.row.fork, why: 'other', at: missAt ?? Date.now() },
  };
}

/** Работа остаётся в списке, даже если кадр или сито её сняли. */
export function keepWork<T extends { big: FeedRow[]; tail: FeedRow[] }>(
  lists: T,
  work: readonly FeedRow[],
  locked: PageLine | null = null,
  tapes: readonly string[] = [],
): T {
  const allowed = (row: FeedRow): boolean => {
    const line = lineOfRow(row.row.fork);
    return (locked === null || line === null || line === locked) && tapeAllowed(row.row.fork, tapes);
  };
  // A paused list can still contain the previous tab's rows.
  if (locked !== null || tapes.length > 0) {
    lists = { ...lists, big: lists.big.filter(allowed), tail: lists.tail.filter(allowed) };
  }
  if (work.length === 0) return lists;
  const overlay = new Map(work.map((row) => [row.id, row]));
  const swap = (rows: FeedRow[]): FeedRow[] => rows.map((row) => overlay.get(row.id) ?? row);
  const big = swap(lists.big);
  const tail = swap(lists.tail);
  const have = new Set<string>();
  for (const row of big) have.add(row.id);
  for (const row of tail) have.add(row.id);
  const missing = work.filter((row) => {
    if (have.has(row.id) || !allowed(row)) return false;
    if (locked === null) return true;
    const line = lineOfRow(row.row.fork);
    return line === null || line === locked;
  });
  if (missing.length === 0) return { ...lists, big, tail };
  return { ...lists, big: [...missing, ...big], tail };
}

/**
 * Открытая, вынесенная и начатая — последние строки с пеленой, если кадра нет.
 * Тишина сокета пелену не поднимает: это не смерть вилки.
 */
export function useHeldWork(
  live: { big: readonly FeedRow[]; tail: readonly FeedRow[] },
  shown: { big: readonly FeedRow[]; tail: readonly FeedRow[] },
): FeedRow[] {
  const lastById = useRef(new Map<string, FeedRow>());
  const missAt = useRef(new Map<string, number>());
  const status = useScreen((s) => s.status);
  const framed = useScreen((s) => s.framed);
  const steps = useScreen((s) => s.steps);
  const open = useScreen((s) => s.open);
  const popped = useScreen((s) => s.popped);
  const hiddenForks = useScreen((s) => s.hiddenForks);
  const held = heldIds({ steps, open, popped, hiddenForks });

  const liveById = new Map<string, FeedRow>();
  for (const row of live.big) liveById.set(row.id, row);
  for (const row of live.tail) liveById.set(row.id, row);
  const shownById = new Map<string, FeedRow>();
  for (const row of shown.big) shownById.set(row.id, row);
  for (const row of shown.tail) shownById.set(row.id, row);

  for (const id of [...lastById.current.keys()]) {
    if (held.has(id)) continue;
    lastById.current.delete(id);
    missAt.current.delete(id);
  }

  const engineLive = status === 'live' && framed;
  const out: FeedRow[] = [];
  for (const id of held) {
    const fromLive = liveById.get(id) ?? null;
    if (fromLive !== null) {
      lastById.current.set(id, fromLive);
      if (fromLive.ghost === null) missAt.current.delete(id);
    } else if (!lastById.current.has(id)) {
      const seed = shownById.get(id);
      if (seed !== undefined) lastById.current.set(id, seed);
    }
    const last = lastById.current.get(id) ?? null;
    if (fromLive === null && last !== null && !missAt.current.has(id)) {
      missAt.current.set(id, Date.now());
    }
    const covered = coverMissing(fromLive, last, missAt.current.get(id) ?? null, engineLive);
    if (covered === null) continue;
    lastById.current.set(id, covered);
    out.push(covered);
  }
  return out;
}
